import { datadogRum, RumEvent, RumInitConfiguration } from '@datadog/browser-rum';

import { Injectable } from '@angular/core';

import type { CustomActionMap } from './custom-action-map';
import type { CUSTOM_TIMINGS } from './custom-timings';
import type { GlobalContextMap } from './global-context-map';
import type { FsRumUser } from './user';

// Match ANY query parameter containing 'token' (case-insensitive) with regular or URL-encoded separators
// Catches: token=, authToken=, refreshToken=, accessToken=, TOKEN=, etc.
const TOKEN_REGEX = /([?&]|%3f|%26)([a-z]*token[a-z]*)(%3d|=)[^&]*/gi;
const TOKEN_REPLACE_VALUE = '$1$2$3REDACTED'; // Preserve separator, param name, and = or %3D

@Injectable({
  providedIn: 'root',
})
export class FsDataDogRumService {
  static beforeSend(event: RumEvent) {
    // Replace ALL token occurrences (including in nested redirect_url parameters)
    if (event?.view?.url) {
      event.view.url = FsDataDogRumService._redactTokensFromUrl(event.view.url);
    }

    if (event?.view?.referrer) {
      event.view.referrer = FsDataDogRumService._redactTokensFromUrl(event.view.referrer);
    }
    return true;
  }

  private static _redactTokensFromUrl(url: string): string {
    const lowerUrl = url.toLowerCase();
    const hasToken = lowerUrl.includes('token=') || lowerUrl.includes('token%3');
    return hasToken ? url.replace(TOKEN_REGEX, TOKEN_REPLACE_VALUE) : url;
  }

  get isEnabled(): boolean {
    return localStorage.getItem('IS_CONTROLLED') === '0';
  }

  constructor() {}

  public initRum(config: RumInitConfiguration): void {
    if (this.isEnabled) {
      datadogRum.init({
        site: 'datadoghq.com',
        sessionReplaySampleRate: 20,
        trackUserInteractions: true,
        trackResources: true,
        trackLongTasks: true,
        defaultPrivacyLevel: 'mask',
        enablePrivacyForActionName: true,
        beforeSend: FsDataDogRumService.beforeSend,
        ...config,
      });

      // https://developer.chrome.com/blog/memory-and-energy-saver-mode#detecting_that_a_tab_was_discarded
      // @ts-expect-error: Chrome specific property to see if the _previous_ session was discarded
      datadogRum.setGlobalContextProperty('document_was_discarded', document.wasDiscarded);

      // Track if document is in the foreground or background
      document.addEventListener('visibilitychange', () => {
        datadogRum.addAction('document_visibility_change', {
          visibilityState: document.visibilityState,
        });
      });
    }
  }

  public startSessionRecording(): void {
    datadogRum.startSessionReplayRecording();
  }

  public stopSessionRecording(): void {
    datadogRum.stopSessionReplayRecording();
  }

  public stopSession(): void {
    datadogRum.stopSession();
  }

  public setGlobalContextProperty<T extends keyof GlobalContextMap>(
    field: T,
    data: GlobalContextMap[T],
  ): void {
    datadogRum.setGlobalContextProperty(field, data);
  }

  public setUser(data: FsRumUser): void {
    datadogRum.setUser(data);
  }

  public setUserProperty<T extends keyof FsRumUser>(field: T, data: FsRumUser[T]): void {
    datadogRum.setUserProperty(field, data);
  }

  public addAction<T extends keyof CustomActionMap, K extends CustomActionMap[T]>(
    name: T,
    context?: K,
  ): void {
    datadogRum.addAction(name, context);
  }

  public addTiming<T extends CUSTOM_TIMINGS>(name: T): void {
    datadogRum.addTiming(name);
  }

  public addError(error: Error, context?: { [key: string]: unknown }): void {
    datadogRum.addError(error, context);
  }
}
